import type { Course } from '@/domain/entities/course.entity'
import type { ContentLanguage } from '@/domain/entities/content-language.entity'
import type { ICourseRepository, GetCoursesParams, GetContentLanguagesParams, GetPopularCoursesParams, GetCategoriesByLocaleParams } from '@/domain/repositories/course.repository'
import type { PaginatedResult } from '@/domain/entities/base.entity'
import { coursesContract } from '@bibleawr/landing2-contract'
import { createLandingTsRestClient } from '../api/ts-rest-client'
import { ApiError } from '../errors'
import type { CourseDetailsResponseDto, ContentLanguageDto } from '../dto/course.dto'
import {
  mapCourseDtoArrayToEntities,
  mapContentLanguageDtoArrayToEntities,
} from '../mappers/course.mapper'
import { toStringOrUndefined } from '@/utils/value.helpers'

export class CourseRepository implements ICourseRepository {
  private readonly client = createLandingTsRestClient(coursesContract)

  async findDetailsBySlug (slug: string): Promise<CourseDetailsResponseDto | null> {
    const res = await this.client.getCourseDetailsBySlug({ params: { slug } })
    if (res.status === 404) return null
    if (res.status !== 200) throw new ApiError(`getCourseDetailsBySlug failed`, res.status)
    const raw = res.body.data
    if (!raw?._id) return null
    return raw as CourseDetailsResponseDto
  }

  async findMany (params?: GetCoursesParams): Promise<PaginatedResult<Course>> {
    const res = await this.client.getCourses({
      query: {
        cursor: params?.cursor,
        limit: params?.limit,
        course_is_published: params?.course_is_published,
        course_owner_account_id: params?.course_owner_account_id,
        course_url_token: params?.course_url_token,
        course_category: params?.course_category,
        course_locale: params?.course_locale,
        sort_by: params?.sort_by,
        order: params?.order,
      },
    })
    if (res.status !== 200) throw new ApiError(`getCourses failed`, res.status)

    const items = mapCourseDtoArrayToEntities(res.body.data ?? [])
    const pagination = res.body.pagination
    const limit = pagination?.limit ?? params?.limit ?? 20
    const cursor = toStringOrUndefined(pagination?.cursor)
    const hasNextPage = pagination?.hasNextPage ?? items.length >= limit

    return { items, cursor, limit, hasNextPage }
  }

  async findContentLanguages (params?: GetContentLanguagesParams): Promise<ContentLanguage[]> {
    const res = await this.client.getContentLanguages({
      query: params?.category ? { category: params.category } : {},
    })
    if (res.status !== 200) throw new ApiError(`getContentLanguages failed`, res.status)
    return mapContentLanguageDtoArrayToEntities((res.body.data ?? []) as ContentLanguageDto[])
  }

  async findPopularCourses (params: GetPopularCoursesParams): Promise<Course[]> {
    const res = await this.client.getPopularCourses({
      query: { locale: params.locale, limit: params.limit },
    })
    if (res.status !== 200) throw new ApiError(`getPopularCourses failed`, res.status)
    return mapCourseDtoArrayToEntities(res.body.data ?? [])
  }

  async findCategoriesByLocale (params: GetCategoriesByLocaleParams): Promise<string[]> {
    const res = await this.client.getCategoriesByLocale({ query: { locale: params.locale } })
    if (res.status !== 200) throw new ApiError(`getCategoriesByLocale failed`, res.status)
    return res.body.data ?? []
  }
}
